import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';
import {
  catchError,
  EMPTY,
  first,
  map,
  Observable,
  Subject,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';

import { environment } from '@Environment/environment';
import { messageData } from '@VineyardLib/data/message-data';
import { BizDateService } from '@VineyardLib/services/biz-date.service';
import { ClientSettingService } from '@VineyardLib/services/client-setting.service';
import { LoginService } from '@VineyardLib/services/login.service';
import { BIZ_DATE, TokenRefreshStatus } from '@VineyardLibConstant/constant';

const assetsPath = './assets/';
const versionCheckUrl = `${assetsPath}package.json?`;

export const PACKAGE_INFO_TOKEN = new InjectionToken<{
  appName: string;
  version: string;
}>('packageInfo');

/**
 * Request用のinterceptor
 */
export const httpRequestInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  return inject(RequestInterceptorService).validateRequest(req, next);
};

@Injectable({
  providedIn: 'root',
})
class RequestInterceptorService {
  private readonly bizDateService = inject(BizDateService);
  private readonly loginService = inject(LoginService);
  private readonly httpClient = inject(HttpClient);
  private readonly locale = inject(ClientSettingService).locale;
  private readonly packageInfo = inject(PACKAGE_INFO_TOKEN);
  // バージョンチェック用
  private newVersionDetectedInMs: number = -1;

  validateRequest(
    request: HttpRequest<unknown>,
    next: HttpHandlerFn
  ): Observable<HttpEvent<unknown>> {
    const callExecuteRequest = () => this.executeRequest(request, next);

    // 以下の場合は、バージョンチェック、セッションチェックを行わず、そのまま返す
    if (
      request.url.startsWith(assetsPath) || // assets系リクエスト（現状は、バージョンチェックjson読み込み、翻訳json読み込み）
      request.url.match(/^\/oauth2\/\w*\/revoke$/) || // ログアウト
      request.url.match(/^\/oauth2\/\w*\/token$/) // トークン発行のURL
    ) {
      return callExecuteRequest();
    }

    // バージョンが異なることが検知済みの場合、エラーを返し、リクエストを送信しないように
    // 参照: https://angular.io/guide/http-intercept-requests-and-responses#the-next-object
    if (this.newVersionDetectedInMs > -1) {
      return throwError(() => this.createVersionCheckHttpError(request));
    }

    const actionAfterExecuteRequest = new Subject<HttpEvent<unknown>>();
    this.isLatestVersion$()
      .pipe(first())
      .subscribe((isLatestVersion) => {
        if (!isLatestVersion) {
          // 最新バージョンでは無いため、バージョンチェックエラーを返す
          this.newVersionDetectedInMs = new Date().getTime();
          actionAfterExecuteRequest.error(
            this.createVersionCheckHttpError(request)
          );

          return;
        }

        // Tokenを利用するリクエストの場合、セッションとTokenのチェックを行う
        if (
          request.url.match(`${environment.vsolServerUrl}`) ||
          request.url.match(`${environment.idcsOAuthUrl}/admin`)
        ) {
          // リフレッシュトークンが切れている場合（Cookieに無い場合も含む）は、ログアウトする
          if (this.loginService.isExpiredRefreshToken()) {
            this.loginService.revoke();
            return;
          }

          // セッションが切れている場合は、ログアウトし、エラーを表示する
          if (this.loginService.isExpiredSession()) {
            this.loginService.revoke();
            actionAfterExecuteRequest.error(
              this.createSessionExpiredHttpError(request)
            );

            return;
          }

          // セッションは切れていないが、アクセストークンが有効期限切れの場合はrefreshする
          if (this.loginService.isExpiredAccessToken()) {
            // リフレッシュ中の場合は、リフレッシュしない
            if (
              this.loginService.tokenRefreshStatus.getValue() !=
              TokenRefreshStatus.InProgress
            ) {
              this.loginService.refresh();
            }

            const unsubscribe = new Subject<boolean>();
            this.loginService.tokenRefreshStatus$
              .pipe(takeUntil(unsubscribe))
              .subscribe((tokenRefreshStatus) => {
                switch (tokenRefreshStatus) {
                  case TokenRefreshStatus.Completed:
                    this.executeRequest(request, next)
                      .pipe(
                        catchError((err) => {
                          actionAfterExecuteRequest.error(err);
                          return EMPTY;
                        })
                      )
                      .subscribe((response) => {
                        actionAfterExecuteRequest.next(response);
                      });
                    unsubscribe.next(true);
                    break;
                  case TokenRefreshStatus.Error:
                    unsubscribe.next(true);
                    break;
                  case TokenRefreshStatus.InProgress:
                  default:
                    break;
                }
              });

            return;
          }
        }

        // 全てのチェックが問題ないとき
        callExecuteRequest()
          .pipe(
            catchError((err) => {
              actionAfterExecuteRequest.error(err);
              return EMPTY;
            })
          )
          .subscribe((response) => {
            actionAfterExecuteRequest.next(response);
          });
      });

    return actionAfterExecuteRequest.asObservable();
  }

  private isLatestVersion$(): Observable<boolean> {
    return this.httpClient
      .get<{
        name: string;
        version: string;
      }>(versionCheckUrl + new Date().getTime())
      .pipe(
        map((remotePackageInfo) => {
          if (remotePackageInfo.version != this.packageInfo.version) {
            return false;
          } else {
            return true;
          }
        })
      );
  }

  private executeRequest(
    request: HttpRequest<unknown>,
    next: HttpHandlerFn
  ): Observable<HttpEvent<unknown>> {
    let modifyRequest = request;
    if (request.url.match(`${environment.idcsOAuthUrl}/admin`)) {
      modifyRequest = this.modifyHttpRequestForIDCSAdminApi(request);
    } else if (request.url.match(`${environment.vsolServerUrl}`)) {
      modifyRequest = this.modifyHttpRequestForServerApi(request);
    } else if (request.url.match(/^\/oauth2\//)) {
      modifyRequest = this.modifyHttpRequestForIDCSApi(request);
    }

    this.developmentLog(modifyRequest);
    return next(modifyRequest).pipe(
      catchError((err) => {
        this.developmentLog(err);
        throw err;
      }),
      tap((success) => {
        this.updateBizDate(success);
        this.developmentLog(success);
        // サーバーAPIの場合、cookieにAPI実行時刻を登録
        if (request.url.match(`${environment.vsolServerUrl}`)) {
          this.loginService.setLastAccessTimeCookie();
        }
      })
    );
  }

  /**
   *
   * @param response
   */
  private updateBizDate(response: HttpEvent<unknown>): void {
    if (response instanceof HttpResponse) {
      if (response.headers.has(BIZ_DATE)) {
        this.bizDateService.bizDate = String(response.headers.get(BIZ_DATE));
      }
    }
  }

  /**
   * 開発ビルド時にコンソールログ出力
   * @param target
   */
  private developmentLog(target: unknown): void {
    if (!environment.production) {
      console.log(target);
    }
  }

  /**
   * RequestにServer APIの共通仕様を反映する
   * @param request HttpRequest<unknown>
   */
  private modifyHttpRequestForServerApi(
    request: HttpRequest<unknown>
  ): HttpRequest<unknown> {
    const newRequest = request.clone({
      headers: request.headers
        .set('Content-Type', 'application/json')
        .set(
          'Authorization',
          `${this.loginService.getToken()?.token_type} ${this.loginService.getToken()?.access_token}`
        )
        .set('Accept-Language', this.locale)
        .set(BIZ_DATE, this.bizDateService.formatYyyyMmDdString),
    });
    return newRequest;
  }

  /**
   * RequestにIDCS OAuth APIの共通仕様を反映する
   * @param request HttpRequest<unknown>
   */
  private modifyHttpRequestForIDCSApi(
    request: HttpRequest<unknown>
  ): HttpRequest<unknown> {
    const newRequest = request.clone({
      url: `${environment.idcsOAuthUrl}${request.url}`,
      headers: request.headers
        .set('Content-Type', 'application/x-www-form-urlencoded')
        .set('Authorization', `Basic ${environment.idcsClientIdAndSecret}`)
        .set('Accept-Language', this.locale),
    });
    return newRequest;
  }

  /**
   * RequestにIDCS Admin APIの共通仕様を反映する
   * @param request HttpRequest<unknown>
   */
  private modifyHttpRequestForIDCSAdminApi(
    request: HttpRequest<unknown>
  ): HttpRequest<unknown> {
    const newRequest = request.clone({
      url: request.url,
      headers: request.headers
        .set('Content-Type', 'application/json')
        .set(
          'Authorization',
          `${this.loginService.getToken()?.token_type} ${this.loginService.getToken()?.access_token}`
        ),
    });
    return newRequest;
  }

  private createVersionCheckHttpError(
    request: HttpRequest<unknown>
  ): HttpErrorResponse {
    let message: messageData;
    const versionErrorMsgArg = { '0': this.packageInfo.appName };
    // ログイン中の場合は、ログアウトを促すメッセージにする
    if (this.loginService.isAuthorized) {
      message = {
        messageKey: 'M608',
        replacementParams: versionErrorMsgArg,
      };
    } else {
      message = {
        messageKey: 'M609',
        replacementParams: versionErrorMsgArg,
      };
    }

    return new HttpErrorResponse({
      error: {
        message: message,
        timestamp: this.newVersionDetectedInMs,
      },
      headers: request.headers,
      status: 0,
      url: request.url,
    });
  }

  private createSessionExpiredHttpError(
    request: HttpRequest<unknown>
  ): HttpErrorResponse {
    return new HttpErrorResponse({
      error: {
        message: 'M614',
        timestamp: new Date().getTime(),
      },
      headers: request.headers,
      status: 0,
      url: request.url,
    });
  }
}
